<script>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Draggable from 'vuedraggable';

export default {
  name: 'FolderOrder',
  components: {
    Draggable,
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();

    const dragging = ref(false);

    const FOLDERS_ORDER_KEY = 'conversation_custom_views_order';

    const customViews = useMapGetter(
      'customViews/getNestedConversationCustomViews'
    );

    const orderedFolders = computed({
      get: () => {
        return customViews.value ?? [];
      },
      set: newOrder => {
        const parentOrder = newOrder.map(parent => parent.id);
        const childrenOrders = {};

        newOrder.forEach(parent => {
          if (parent.children && parent.children.length > 0) {
            childrenOrders[parent.id] = parent.children.map(child => child.id);
          }
        });

        updateUISettings({
          [FOLDERS_ORDER_KEY]: {
            parents: parentOrder,
            children: childrenOrders,
          },
        });
      },
    });

    const updateChildOrder = (parentId, newChildOrder) => {
      const currentOrder = uiSettings.value?.[FOLDERS_ORDER_KEY] || {};

      // Handle both old array format and new nested format
      let orderSettings;
      if (Array.isArray(currentOrder)) {
        orderSettings = {
          parents: currentOrder,
          children: {},
        };
      } else {
        orderSettings = { ...currentOrder };
      }

      if (!orderSettings.children) {
        orderSettings.children = {};
      }
      orderSettings.children[parentId] = newChildOrder.map(child => child.id);

      updateUISettings({
        [FOLDERS_ORDER_KEY]: orderSettings,
      });
    };

    const onDragEnd = () => {
      dragging.value = false;
    };

    const onDragStart = () => {
      dragging.value = true;
    };

    return {
      orderedFolders,
      dragging,
      onDragEnd,
      onDragStart,
      updateChildOrder,
    };
  },
};
</script>

<template>
  <div class="space-y-4">
    <div
      v-if="!orderedFolders.length"
      class="text-sm text-slate-600 dark:text-slate-400"
    >
      {{ $t('PROFILE_SETTINGS.FORM.FOLDER_ORDER.NO_FOLDERS') }}
    </div>

    <Draggable
      v-else
      v-model="orderedFolders"
      class="space-y-2"
      animation="200"
      ghost-class="ghost"
      handle=".drag-handle"
      item-key="id"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <template #item="{ element }">
        <div class="folder-group">
          <!-- Parent Folder -->
          <div
            class="flex items-center p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-grab drag-handle"
            :class="{ 'cursor-grabbing': dragging }"
          >
            <div class="flex items-center flex-1 space-x-3">
              <!-- Drag handle icon -->
              <div
                class="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-400"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M7 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"
                  />
                </svg>
              </div>

              <!-- Folder icon and name -->
              <div class="flex items-center flex-1 space-x-2">
                <!-- Folder icon -->
                <svg
                  class="w-4 h-4 text-slate-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                  />
                </svg>

                <p
                  class="text-sm font-medium text-slate-900 dark:text-slate-100"
                >
                  {{ element.name }}
                </p>

                <!-- Children count badge -->
                <span
                  v-if="element.children && element.children.length > 0"
                  class="inline-flex items-center px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-full"
                >
                  {{ element.children.length }}
                </span>
              </div>
            </div>
          </div>

          <!-- Child Folders (Draggable) -->
          <div
            v-if="element.children && element.children.length > 0"
            class="ml-8 mt-2"
          >
            <Draggable
              :model-value="element.children"
              class="space-y-1"
              animation="200"
              ghost-class="ghost-child"
              handle=".child-drag-handle"
              item-key="id"
              group="children"
              @update:model-value="
                newChildOrder => updateChildOrder(element.id, newChildOrder)
              "
              @start="onDragStart"
              @end="onDragEnd"
            >
              <template #item="{ element: child }">
                <div
                  class="flex items-center p-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-grab child-drag-handle"
                  :class="{ 'cursor-grabbing': dragging }"
                >
                  <div class="flex items-center flex-1 space-x-2">
                    <!-- Child drag handle -->
                    <div
                      class="text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400"
                    >
                      <svg
                        class="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M7 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"
                        />
                      </svg>
                    </div>

                    <!-- Child folder icon -->
                    <svg
                      class="w-3 h-3 text-slate-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                      />
                    </svg>

                    <span class="text-slate-700 dark:text-slate-300">
                      {{ child.name }}
                    </span>
                  </div>
                </div>
              </template>
            </Draggable>
          </div>
        </div>
      </template>
    </Draggable>
  </div>
</template>

<style scoped>
.ghost {
  opacity: 0.5;
  background-color: #f1f5f9;
}

.dark .ghost {
  background-color: #334155;
}

.ghost-child {
  opacity: 0.5;
  background-color: #e2e8f0;
  transform: rotate(2deg);
}

.dark .ghost-child {
  background-color: #475569;
}

.drag-handle:hover {
  background-color: #f8fafc;
}

.dark .drag-handle:hover {
  background-color: #334155;
}

.child-drag-handle:hover {
  background-color: #f1f5f9;
}

.dark .child-drag-handle:hover {
  background-color: #475569;
}
</style>
